import React from "react";
import { Box, Slider, Typography } from "@mui/material";
import { StyledTextField } from "./StyledTextField";
import { QUIZ } from "@/theme/quizTokens";
import { quizAsset } from "../shared/quizAssets";

interface WeightSliderProps {
    value: number | "";
    onChange: (v: number | "") => void;
}

/** Bottom-aligned dog silhouettes (chihuahua → great dane) above the slider, per Figma. */
const SILHOUETTES = [
    { key: "icon-weight-1", h: { xs: 22, md: 30 } },
    { key: "icon-weight-2", h: { xs: 28, md: 40 } },
    { key: "icon-weight-3", h: { xs: 34, md: 48 } },
    { key: "icon-weight-4", h: { xs: 40, md: 58 } },
    { key: "icon-weight-5", h: { xs: 48, md: 70 } },
    { key: "icon-weight-6", h: { xs: 56, md: 82 } },
];

export const WeightSlider: React.FC<WeightSliderProps> = ({ value, onChange }) => {
    const safeValue = value === "" ? 0 : Number(value);

    // Slider moved
    const handleSlider = (_: any, newValue: number | number[]) => {
        const v = Array.isArray(newValue) ? newValue[0] : newValue;
        onChange(v);
    };

    // User typed in input
    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;

        if (raw === "") return onChange("");

        const num = Number(raw);
        if (isNaN(num)) return;

        const clamped = Math.max(1, Math.min(60, num));
        onChange(clamped);
    };

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
            }}
        >
            {/* Dog size scale — silhouettes bottom-aligned, small → large */}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                    px: { xs: "2px", md: "6px" },
                }}
            >
                {SILHOUETTES.map((s) => (
                    <Box
                        key={s.key}
                        component="img"
                        src={quizAsset(s.key)}
                        alt=""
                        sx={{ height: s.h, width: "auto", display: "block" }}
                    />
                ))}
            </Box>

            {/* 0kg / 60kg scale labels */}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    px: "4px",
                    mt: "2px",
                }}
            >
                <Typography sx={{ fontFamily: QUIZ.font.body }} fontSize={15} fontWeight={700} color={QUIZ.color.ink}>
                    0kg
                </Typography>
                <Typography sx={{ fontFamily: QUIZ.font.body }} fontSize={15} fontWeight={700} color={QUIZ.color.ink}>
                    60kg
                </Typography>
            </Box>

            {/* Slider — white round thumb on a light rail with interval dots */}
            <Box sx={{ width: "100%", px: 1 }}>
                <Slider
                    value={safeValue}
                    min={0}
                    max={60}
                    step={0.5}
                    marks={[12, 24, 36, 48].map((v) => ({ value: v }))}
                    onChange={handleSlider}
                    sx={{
                        color: QUIZ.color.blue,
                        height: 7,
                        py: "14px",
                        "& .MuiSlider-thumb": {
                            width: 34,
                            height: 24,
                            borderRadius: "100px",
                            backgroundColor: QUIZ.color.white,
                            boxShadow: "0px 1px 4px rgba(0,0,0,0.25)",
                            "&:hover, &.Mui-focusVisible": {
                                boxShadow: "0px 1px 6px rgba(0,0,0,0.3)",
                            },
                        },
                        "& .MuiSlider-track": {
                            height: 7,
                            border: "none",
                            backgroundColor: QUIZ.color.blue,
                        },
                        "& .MuiSlider-rail": {
                            height: 7,
                            opacity: 1,
                            backgroundColor: "#e3ddd5",
                        },
                        "& .MuiSlider-mark": {
                            width: 3,
                            height: 3,
                            borderRadius: "50%",
                            backgroundColor: "#c9c2ba",
                            opacity: 1,
                        },
                    }}
                />
            </Box>

            {/* INPUT BOX BELOW — full width per Figma */}
            <StyledTextField
                type="number"
                value={value}
                onChange={handleInput}
                placeholder="Kg"
                inputProps={{ min: 1, max: 100 }}
                fullWidth
            />
        </Box>
    );
};